import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '@/layouts/AuthLayout';
import { Field, inputClass } from '@/components/forms/form';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { apiFetch, ApiError } from '@/lib/api/client';

const LANGUAGES = ['Hindi', 'English', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Punjabi', 'Urdu'];

interface ApplicationStatus {
  application: { status: string; applied_at: string | null; review_notes: string | null } | null;
}

/**
 * Companion application — invite-gated, and requires an account.
 *
 * Companion is a role granted on top of a member account, not a separate
 * signup. So this page asks you to sign in first, then to apply.
 */
export default function CompanionApply() {
  const navigate = useNavigate();
  const { session, loading, refreshProfile } = useAuth();

  const [invite, setInvite] = useState('');
  const [legalName, setLegalName] = useState('');
  const [dob, setDob] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [bio, setBio] = useState('');
  const [consent, setConsent] = useState(false);

  const [existing, setExisting] = useState<ApplicationStatus['application']>(null);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Don't show the form to someone who already applied.
  useEffect(() => {
    if (loading || !session) {
      setChecking(false);
      return;
    }
    let active = true;
    void apiFetch<ApplicationStatus>('/api/companion/me')
      .then((r) => {
        if (active) setExisting(r.application);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [loading, session]);

  const toggle = (l: string) =>
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  const canSubmit =
    invite.trim().length >= 4 &&
    legalName.trim().length >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(dob) &&
    languages.length > 0 &&
    consent;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError('');
    try {
      await apiFetch('/api/companion/apply', {
        method: 'POST',
        body: {
          inviteCode: invite.trim(),
          legalName: legalName.trim(),
          bio: bio.trim() || undefined,
          languages,
          dateOfBirth: dob,
        },
      });
      await refreshProfile();
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <AuthLayout
      image="auth-companion.jpg"
      imageAlt="A woman on a terrace in morning light with a mug of chai"
      quote="We pay a fixed rupee rate per minute. It does not change when we run promotions."
      attribution="How companions are paid"
    >
      {children}
    </AuthLayout>
  );

  if (loading || checking) {
    return shell(<p className="text-sm text-[var(--muted)]">Loading…</p>);
  }

  // ── Not signed in ───────────────────────────────────────────────────────
  if (!session) {
    return shell(
      <>
        <span
          className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: 'var(--teal-soft)', color: 'var(--teal)' }}
        >
          Step 1 of 2
        </span>
        <h1 className="mt-4 text-3xl font-semibold">First, create your account</h1>
        <p className="mt-2.5 text-[var(--ink-2)]">
          Becoming a companion is an approval on top of a normal account. Sign up or log in, then
          come back here with your invite code.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <Button to="/signup" size="lg" full>
            Create an account
          </Button>
          <Button to="/login" variant="secondary" size="lg" full>
            I already have one
          </Button>
        </div>
        <p className="mt-6 text-xs text-[var(--muted)]">
          You will need an invite code from our team to complete the application.
        </p>
      </>,
    );
  }

  // ── Already applied ─────────────────────────────────────────────────────
  if (submitted || existing) {
    const status = submitted ? 'pending_review' : (existing?.status ?? 'pending_review');
    const approved = status === 'approved';
    const rejected = status === 'rejected';

    return shell(
      <>
        <div
          className="grid size-12 place-items-center rounded-full"
          style={{
            background: rejected ? 'var(--danger-soft)' : 'var(--online-soft)',
            color: rejected ? 'var(--danger)' : 'var(--online)',
          }}
        >
          <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden>
            <path
              d={rejected ? 'M7 7l10 10M17 7L7 17' : 'M5 13l4 4L19 7'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="mt-5 text-3xl font-semibold">
          {approved
            ? 'You are approved'
            : rejected
              ? 'Application not accepted'
              : 'Application received'}
        </h1>

        <p className="mt-3 text-[var(--ink-2)]">
          {approved
            ? 'Your companion dashboard is open. Go online whenever you are ready.'
            : rejected
              ? (existing?.review_notes ??
                'We are not able to move forward at this time. You may contact us if you think this is a mistake.')
              : 'We review applications within two working days. We will email you either way.'}
        </p>

        <div className="mt-8">
          {approved ? (
            <Button size="lg" onClick={() => navigate('/companion')}>
              Open dashboard
            </Button>
          ) : rejected ? (
            <Button to="/contact" variant="secondary" size="lg">
              Contact us
            </Button>
          ) : (
            <Button to="/" variant="secondary" size="lg">
              Back to home
            </Button>
          )}
        </div>
      </>,
    );
  }

  // ── The form ────────────────────────────────────────────────────────────
  return shell(
    <>
      <span
        className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
        style={{ background: 'var(--teal-soft)', color: 'var(--teal)' }}
      >
        Step 2 of 2 · Invite only
      </span>

      <h1 className="mt-4 text-3xl font-semibold">Apply to be a companion</h1>
      <p className="mt-2.5 text-[var(--ink-2)]">
        We onboard a small group at a time so we can verify everyone properly.
      </p>

      <form
        className="mt-8 flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field id="invite" label="Invite code" error={error} hint="Sent to you by our team.">
          <input
            id="invite"
            type="text"
            autoFocus
            placeholder="MMP-XXXX"
            className={`${inputClass} font-[family-name:var(--font-mono)] tracking-wider uppercase`}
            value={invite}
            onChange={(e) => {
              setInvite(e.target.value.toUpperCase());
              setError('');
            }}
          />
        </Field>

        <Field id="legalName" label="Full legal name" hint="As it appears on your PAN card.">
          <input
            id="legalName"
            type="text"
            autoComplete="name"
            placeholder="Ananya Rao"
            className={inputClass}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
        </Field>

        <Field id="dob" label="Date of birth" hint="You must be 18 or older.">
          <input
            id="dob"
            type="date"
            className={inputClass}
            value={dob}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDob(e.target.value)}
          />
        </Field>

        <div>
          <span className="mb-2 block text-sm font-medium">Languages you can talk in</span>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((l) => {
              const on = languages.includes(l);
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggle(l)}
                  aria-pressed={on}
                  className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-150 hover:-translate-y-px"
                  style={{
                    background: on ? 'var(--marigold-soft)' : 'var(--surface)',
                    borderColor: on ? 'var(--marigold)' : 'var(--line)',
                    color: on ? 'var(--marigold)' : 'var(--ink-2)',
                  }}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>

        <Field id="bio" label="A little about you" hint="Optional. Members will read this.">
          <textarea
            id="bio"
            rows={3}
            maxLength={2000}
            placeholder="What are you good at listening to?"
            className={`${inputClass} resize-none`}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
        </Field>

        <label className="flex cursor-pointer items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 size-4.5 shrink-0 accent-[var(--marigold)]"
          />
          <span className="text-[var(--ink-2)]">
            I am 18 or older and I consent to identity verification (PAN and one photo ID).
          </span>
        </label>

        <Button size="lg" full type="submit" disabled={!canSubmit || busy}>
          {busy ? 'Submitting…' : 'Submit application'}
        </Button>

        <p className="text-xs leading-relaxed text-[var(--muted)]">
          We review within two working days. Verification is required before your first call or any
          payout.
        </p>
      </form>

      <p className="mt-7 text-sm text-[var(--ink-2)]">
        No invite code?{' '}
        <Link to="/contact" className="font-medium text-[var(--marigold)] hover:underline">
          Get in touch
        </Link>
      </p>
    </>,
  );
}
